/* eslint-disable class-methods-use-this */
import { capitalize } from 'lodash';
import Vue from 'vue';
/**
 * File that contains general logic to power any class that needs to
 * make use of an itemApi and itemGroupApi
*/
import CollectionModule from '../CollectionModule';

export default class DGReviewCollectionGeneric extends CollectionModule {
  constructor(itemApi, itemGroupApi, idField, nameField) {
    super(itemApi, idField, '');

    this.itemGroupApi = itemGroupApi;
    this.itemApi = this.Api;
    this.nameField = nameField;
  }

  extractModule() {
    const module = super.extractModule();

    module.getters.getSuggestions = (...params) => this.getSuggestions(...params);

    module.actions.acceptSuggestions = (...params) => this.acceptSuggestions(...params);
    module.actions.cleanSuggestions = (...params) => this.cleanSuggestions(...params);
    module.actions.refreshCollection = (...params) => this.refreshCollection(...params);
    module.actions.selectItem = (...params) => this.selectItem(...params);
    module.actions.selectAllItems = (...params) => this.selectAllItems(...params);
    module.actions.setFetchSuggestionsTimer = (...params) => this
      .setFetchSuggestionsTimer(...params);
    module.actions.setPaginationSkip = (...params) => this.setPaginationSkip(...params);
    module.actions.setGroupId = (...params) => this.setGroupId(...params);
    module.actions.setSuggestions = (...params) => this.setSuggestions(...params);
    module.actions.setSuggestionsTotalCount = (...params) => this
      .setSuggestionsTotalCount(...params);
    module.actions.unselectAllItems = (...params) => this.unselectAllItems(...params);
    module.actions.unselectItem = (...params) => this.unselectItem(...params);

    module.mutations.RESET_COLLECTION = (...params) => this.RESET_COLLECTION(...params);
    module.mutations.SELECT_ALL_ITEMS = (...params) => this.SELECT_ALL_ITEMS(...params);
    module.mutations.SET_FETCH_SUGGESTIONS_TIMER = (...params) => this
      .SET_FETCH_SUGGESTIONS_TIMER(...params);
    module.mutations.SET_SUGGESTIONS = (...params) => this.SET_SUGGESTIONS(...params);
    module.mutations.SET_SUGGESTIONS_TOTAL_COUNT = (...params) => this
      .SET_SUGGESTIONS_TOTAL_COUNT(...params);
    module.mutations.SET_GROUP_ID = (...params) => this.SET_GROUP_ID(...params);
    module.mutations.SET_MEMBER_QUERY = (...params) => this.SET_MEMBER_QUERY(...params);
    module.mutations.UNSELECT_ALL_ITEMS = (...params) => this.UNSELECT_ALL_ITEMS(...params);

    return module;
  }

  getInitialState() {
    const state = super.getInitialState();

    state.features.allSuggestions = null;
    state.features.fetchSuggestionsTimer = 0;
    state.features.groupId = '';
    state.features.memberQuery = {};

    return state;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  getSuggestions(state) {
    return state.features.allSuggestions;
  }

  memberQuery(state) {
    return state.features.memberQuery;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * We can use `this.itemGroupApi` here, allowing us to put this action
   * inside the "Generic" class
   */
  acceptSuggestions(_, { id, payload }) {
    return this.itemGroupApi.acceptSuggestions(id, payload);
  }

  cleanSuggestions({ dispatch }) {
    dispatch('setSuggestions', null);
  }

  /**
   * All usages of the word "user" or "userGroup" have been genericized.
   * fetch Collection of suggestions using itemApi and itemGroupApi.
   * We can configure this by passing the api we want to use in the instance of the class
   * that extends from this one.
   *
   * This function is handling suggestion pagination by calling the passed itemGroup Api
   * and then searching properties related to that suggestion.
   * The collection contains systems / system users with op property ( add ,remove) and
   * dynamic properties depending on what rules were selected.
   */
  async fetchCollection(context, { next = false } = {}) {
    return new Promise((resolve, reject) => {
      /**
       * It controls if the process will take 10 or 0 seconds
       * to accomplish, it depends if there are changes left or not.
       */
      const timer = context.state.features.fetchSuggestionsTimer || 0;

      setTimeout(async () => {
        // getting group id to use it in fetch itemGroup Api call
        const { groupId } = context.state.features;

        // default pagination properties
        let { skip } = context.state.features.items.pagination;
        let { limit } = context.state.features.items.pagination;

        let params = {
          skip,
          limit,
        };

        // Suggestions can't be fetched for groups without saved filters
        if (!context.state.features.memberQuery) resolve([]);

        // Calling itemGroup api with pagination
        const { results, totalCount } = await this.itemGroupApi
          .fetchSuggestions(groupId, { params });
        const allSuggestions = results;

        // saving new skip value to load more items later
        if (next === true) {
          skip += limit;

          // @todo: Don't call class instance method. Use dispatch or commit
          this.setPaginationSkip(context, { skip });
        }

        // checking if total Count is being assigned correctly and once
        if (context.state.entities.items.totalCount !== totalCount) {
          // @todo: Don't call class instance method. Use dispatch or commit
          this.setSuggestionsTotalCount(context, { totalCount });
        }

        // if we do not need to do more pagination, stop and resolve.
        if (skip > context.state.entities.items.totalCount) {
          resolve();
          return;
        }

        /**
         * Getting all suggestions object, in this object is stored ALL
         * suggestions in order to use it in selectAllItems() method.
         */
        const suggestions = context.state.features.allSuggestions;

        // checking if allSuggestions property is being assigned once.
        if (suggestions === null) {
          // default pagination values to fetch all suggestions
          skip = 0;
          limit = 0;

          params = {
            skip,
            limit,
          };

          // calling `fetchSuggestions` through the itemGroup API
          const data = await this.itemGroupApi.fetchSuggestions(groupId, { params });

          // @todo: Don't call class instance method. Use dispatch or commit
          this.setSuggestions(context, data.results);
        }

        // mapping suggestions ids to filter items later
        const ids = allSuggestions.map(item => ({
          _id: item.object.id,
        }));

        const filter = ids.length ? { or: ids } : undefined;

        let items = {
          results: [],
        };

        if (filter) {
          // Calling item api using filtered suggestions ids
          items = await this.itemApi.filter({ filter, limit })
            .catch((error) => {
              reject(error);
            });
        }

        // Here's a variation
        const { itemsToAdd, itemsToRemove } = items.results.reduce((acc, curItem) => {
          const suggestion = allSuggestions.find(
            item => item.object.id === (curItem.id || curItem._id),
          ) || undefined;

          if (suggestion) {
            // getting suggested operation
            const item = {
              ...curItem,
              suggestion: capitalize(suggestion.op),
            };

            // adding item to array in order to sort it later
            if (suggestion.op === 'add') {
              acc.itemsToAdd.push(item);
            } else {
              acc.itemsToRemove.push(item);
            }
          }

          return acc;
        }, { itemsToAdd: [], itemsToRemove: [] });

        const compare = (a, b) => a[this.nameField].localeCompare(b[this.nameField]);

        itemsToAdd.sort(compare);
        itemsToRemove.sort(compare);

        const totalItems = itemsToAdd.concat(itemsToRemove);

        // collection payload
        const payload = {
          results: totalItems,
          totalCount: totalItems.length || 0,
          totalCountOfAllItems: totalCount || 0,
        };

        context.commit('SET_COLLECTION', payload);

        // reset of timer to avoid 10 seconds delay again in pagination
        context.dispatch('setFetchSuggestionsTimer', 0);

        // return of item ids
        resolve(totalItems.map(systemUser => systemUser.id));
      }, timer);
    });
  }

  refreshCollection({ dispatch }) {
    return dispatch('fetchCollection');
  }

  selectAllItems({ commit, state }) {
    const { items } = state.entities;
    const { allSuggestions } = state.features;
    let ids = Object.keys(items.byId);

    /**
     * By default, this will select first 50 items, with this code
     * it is selecting ALL items in place.
     */
    if (allSuggestions
      && allSuggestions.length && allSuggestions.length > 50) {
      ids = allSuggestions.map(item => item.object.id);
    }

    commit('SELECT_ALL_ITEMS', ids);
  }

  setGroupId({ commit }, groupId) {
    commit('SET_GROUP_ID', groupId);
  }

  selectItem({ commit }, { id }) {
    commit('SELECT_ITEM', { id });
  }

  setFetchSuggestionsTimer({ commit }, timer) {
    commit('SET_FETCH_SUGGESTIONS_TIMER', timer);
  }

  setPaginationSkip({ commit }, { skip }) {
    commit('SET_PAGINATION_SKIP', { skip });
  }

  setSuggestions({ commit }, allSuggestions) {
    commit('SET_SUGGESTIONS', allSuggestions);
  }

  setSuggestionsTotalCount({ commit }, totalCount) {
    commit('SET_SUGGESTIONS_TOTAL_COUNT', totalCount);
  }

  unselectItem({ commit }, { id }) {
    commit('UNSELECT_ITEM', { id });
  }

  unselectAllItems({ commit, state }) {
    const { items } = state.entities;
    const { allSuggestions } = state.features;
    let ids = Object.keys(items.byId);

    /**
     * By default, this will select first 50 items, with this code
     * it is selecting ALL items in place.
     */
    if (allSuggestions
      && allSuggestions.length && allSuggestions.length > 50) {
      ids = allSuggestions.map(item => item.object.id);
    }

    commit('UNSELECT_ALL_ITEMS', ids);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SELECT_ALL_ITEMS(state, ids) {
    Vue.set(state.features.selectedItems, 'ids', ids);
    Vue.set(state.features.unselectedItems, 'ids', []);
  }

  SET_FETCH_SUGGESTIONS_TIMER(state, payload) {
    Vue.set(state.features, 'fetchSuggestionsTimer', payload);
  }

  SET_GROUP_ID(state, groupId) {
    Vue.set(state.features, 'groupId', groupId);
  }

  SET_MEMBER_QUERY(state, memberQuery) {
    Vue.set(state.features, 'memberQuery', memberQuery);
  }

  SET_SUGGESTIONS(state, payload) {
    Vue.set(state.features, 'allSuggestions', payload);
  }

  SET_SUGGESTIONS_TOTAL_COUNT(state, payload) {
    Vue.set(state.entities.items, 'totalCount', payload);
  }

  UNSELECT_ALL_ITEMS(state, ids) {
    Vue.set(state.features.selectedItems, 'ids', []);
    Vue.set(state.features.unselectedItems, 'ids', ids);
  }
}
